import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Info, X } from "lucide-react";

// Persistent point-of-interaction AI disclosure for the Chat surface.
// Deliberately scoped to the four things product/legal actually needs
// to say at the point of use — not a legal restatement. Legal detail
// lives at /gizlilik and /kosullar which this banner links to.
//
// Behaviour:
//   * Persistent slim banner shown ABOVE the chat thread on every
//     session — it is the point-of-interaction disclosure and cannot
//     be permanently dismissed. Users may only collapse it into a
//     smaller footprint via ``sz_ai_disclosure_collapsed``.
//   * On first ever mount per browser, additionally surface a one-time
//     toast so returning learners are actively informed once; guarded
//     by ``sz_ai_disclosure_ack`` in localStorage.

const ACK_KEY = "sz_ai_disclosure_ack";
const COLLAPSE_KEY = "sz_ai_disclosure_collapsed";

export function AiDisclosure() {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(COLLAPSE_KEY) === "1");
    } catch { /* ignore */ }

    try {
      if (localStorage.getItem(ACK_KEY) !== "1") {
        toast.info(
          "Yanıtlar yapay zekâ tarafından üretilir; öğrenme desteği amaçlıdır ve hata içerebilir.",
          { duration: 6500 },
        );
        localStorage.setItem(ACK_KEY, "1");
      }
    } catch { /* ignore */ }
  }, []);

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  }

  return (
    <div
      className="border-b border-amber-100 bg-amber-50/70 text-amber-900"
      data-testid="ai-disclosure"
      role="note"
      aria-label="Yapay zekâ bildirimi"
    >
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-2 flex items-start gap-2 text-[12.5px] leading-snug">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" aria-hidden />
        {collapsed ? (
          <div className="flex-1 flex items-center justify-between gap-2">
            <span data-testid="ai-disclosure-short">
              Yanıtlar yapay zekâ tarafından üretilir; hata içerebilir. Sınav ya da sağlık kararlarınızda öğretmeninize / uzmana danışın.
            </span>
            <button
              type="button" onClick={toggle}
              className="shrink-0 text-[11px] font-medium underline underline-offset-2 hover:text-amber-950"
              data-testid="ai-disclosure-expand"
            >
              Ayrıntı
            </button>
          </div>
        ) : (
          <div className="flex-1">
            <div data-testid="ai-disclosure-full">
              <strong>Yapay zekâ destekli öğrenme:</strong>{" "}
              Yanıtlar yapay zekâ tarafından üretilir veya desteklenir; hata içerebilir.
              SuperZeka, sınavlarda başarıya yönelik <em>öğrenme desteği</em> sunar; psikolojik
              veya tıbbi teşhis, hukuki danışmanlık ya da bir öğretmenin / uzmanın yerini almaz.
              Yüksek riskli veya belirsiz konularda öğretmeninize ya da ilgili nitelikli uzmana
              danışın.{" "}
              <Link
                to="/gizlilik"
                className="underline underline-offset-2 hover:text-amber-950"
                data-testid="ai-disclosure-privacy-link"
              >
                Gizlilik
              </Link>{" · "}
              <Link
                to="/kosullar"
                className="underline underline-offset-2 hover:text-amber-950"
                data-testid="ai-disclosure-terms-link"
              >
                Kullanım Koşulları
              </Link>.
            </div>
            <button
              type="button" onClick={toggle}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 hover:text-amber-950"
              data-testid="ai-disclosure-collapse"
              aria-label="Bildirimi daralt"
            >
              <X className="w-3 h-3" /> Daralt
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
